// Reception-only intake page. Restricted to name/age/gender/referred-by/mobile,
// vitals (weight/height/abd-circ/BP/PR/SpO2), and file uploads (patient photo +
// report). Everything else stays on the doctor's PatientDetail page.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast, Toaster } from "sonner";
import { CLINIC, LogoTile } from "../lib/clinic";
import { useAuth } from "../lib/auth";
import { computeAgeYears } from "../lib/ageUtils";
import ManualLabEntry from "../components/ManualLabEntry";
import {
  Search, UserPlus, Camera, FileUp, Save, LogOut, Loader2,
  Weight, Ruler, Activity, Heart, Droplets, ArrowRight,
} from "lucide-react";

import { PatientLanguageSelect } from "../components/PatientLanguageSelect";

const emptyPatient = { name: "", age: "", sex: "Male", mobile: "", dob: "", referred_by: "", confirm_family_new: false, wa_opt_in: true, preferred_language: "mr" };
const emptyIntake = { weight: "", height: "", waist: "", sbp: "", dbp: "", pulse: "", spo2: "" };

function computeBmi(w, h) {
  const wt = parseFloat(w), ht = parseFloat(h);
  if (!wt || !ht) return "";
  const bmi = wt / Math.pow(ht / 100, 2);
  return isFinite(bmi) ? bmi.toFixed(1) : "";
}

export default function Reception() {
  const nav = useNavigate();
  const { me, logout } = useAuth();
  const [tab, setTab] = useState("search"); // search | new
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [patient, setPatient] = useState(null); // selected patient
  const [form, setForm] = useState(emptyPatient);
  const [vitals, setVitals] = useState(emptyIntake);
  const [familyConfirm, setFamilyConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await api.get(`/patients/search?q=${encodeURIComponent(q)}`);
      setResults(r.data.results || []);
    } catch { toast.error("Search failed"); }
    setSearching(false);
  };

  const registerNew = async (confirmFamily = false) => {
    if (!form.name || !form.mobile) return toast.error("Name and mobile are required");
    if (form.dob) {
      const todayIso = new Date().toISOString().slice(0, 10);
      if (form.dob > todayIso) return toast.error("Date of birth cannot be in the future");
    }
    try {
      const r = await api.post("/patients", { ...form, confirm_family_new: confirmFamily });
      if (r.data.needs_family_confirm) {
        setFamilyConfirm({ family_members: r.data.family_members || [] });
        return;
      }
      const p = r.data.patient;
      setPatient(p);
      setForm(emptyPatient);
      setFamilyConfirm(null);
      setTab("search");
      toast.success(`Patient ${p.name} registered — enter vitals below`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save patient"); }
  };

  const saveIntake = async () => {
    if (!patient) return toast.error("Select a patient first");
    setSaving(true);
    try {
      const bmi = computeBmi(vitals.weight, vitals.height);
      await api.post("/visits", {
        patient_id: patient.patient_id,
        vitals: { ...vitals, bmi },
        chief_complaints: "",
        // status='intake' is enforced server-side for reception role
      });
      toast.success("Intake saved — doctor can see it live now");
      setVitals(emptyIntake);
    } catch { toast.error("Could not save intake"); }
    setSaving(false);
  };

  const uploadPhoto = async (e) => {
    if (!patient) return toast.error("Select a patient first");
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("patient_id", patient.patient_id);
      await api.post("/patients/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Patient photo uploaded");
      // Refresh patient to display new photo
      const r = await api.get(`/patients/${patient.patient_id}`);
      setPatient(r.data.patient);
    } catch { toast.error("Photo upload failed"); }
    setPhotoUploading(false);
    e.target.value = "";
  };

  const uploadReport = async (e) => {
    if (!patient) return toast.error("Select a patient first");
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("patient_id", patient.patient_id);
      fd.append("category", "Laboratory Reports");
      fd.append("auto_extract", "1");
      await api.post("/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Report ${f.name} uploaded — AI is extracting lab values (visible in doctor's Investigations table shortly)`);
    } catch { toast.error("Report upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const bmi = computeBmi(vitals.weight, vitals.height);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogoTile />
          <div>
            <div className="text-sm font-heading font-semibold text-slate-900">Reception Intake</div>
            <div className="text-[11px] text-slate-500">{CLINIC.name} · {me?.name || me?.email}</div>
          </div>
        </div>
        <button onClick={logout} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-red-600 px-2 py-1 rounded hover:bg-slate-100" data-testid="reception-logout"><LogOut size={13} /> Log out</button>
      </header>

      <main className="max-w-5xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: patient selection */}
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setTab("search")}
              className={`px-3 py-1.5 text-xs rounded ${tab === "search" ? "bg-pink-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              data-testid="tab-search"
            >
              <Search size={12} className="inline mr-1" /> Search patient
            </button>
            <button
              onClick={() => setTab("new")}
              className={`px-3 py-1.5 text-xs rounded ${tab === "new" ? "bg-pink-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              data-testid="tab-new"
            >
              <UserPlus size={12} className="inline mr-1" /> New patient
            </button>
          </div>

          {tab === "search" ? (
            <div>
              <div className="flex gap-2 mb-3">
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Search by name or mobile"
                  className="h-9"
                  data-testid="reception-search-input"
                />
                <Button onClick={search} disabled={searching} className="bg-pink-600 hover:bg-pink-700 text-white h-9" data-testid="reception-search-btn">
                  {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                </Button>
              </div>
              <div className="space-y-1.5 max-h-80 overflow-auto" data-testid="reception-search-results">
                {results.map((p) => (
                  <button
                    key={p.patient_id}
                    onClick={() => setPatient(p)}
                    className={`w-full text-left border rounded-md p-2.5 text-xs transition-colors ${patient?.patient_id === p.patient_id ? "border-pink-400 bg-pink-50" : "border-slate-200 hover:border-pink-300 hover:bg-pink-50/50"}`}
                    data-testid={`reception-select-${p.patient_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{p.name}</div>
                      <div className="text-[10px] text-slate-500 tabular">{p.patient_id}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {p.age && <span>Age {p.age} · </span>}
                      {p.sex}
                      {p.mobile && <span> · {p.mobile}</span>}
                    </div>
                  </button>
                ))}
                {results.length === 0 && !searching && (
                  <div className="text-xs text-slate-500 text-center py-6">Type a name or mobile, then Search.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {["name", "mobile"].map((k) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}<span className="text-red-500">*</span></label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 mt-0.5" data-testid={`reception-new-${k}`} />
                </div>
              ))}
              <label className="flex items-center gap-1.5 text-[10.5px] text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={form.wa_opt_in} onChange={(e) => setForm({ ...form, wa_opt_in: e.target.checked })} className="accent-emerald-600" data-testid="reception-new-wa-optin" />
                Consents to WhatsApp messages (reminders, reports, education)
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Age</label>
                  <Input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="h-9 mt-0.5" data-testid="reception-new-age" placeholder={form.dob ? "auto" : ""} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">DOB</label>
                  <Input
                    type="date"
                    max={new Date().toISOString().slice(0, 10)}
                    value={form.dob}
                    onChange={(e) => {
                      const dob = e.target.value;
                      const derived = computeAgeYears(dob);
                      setForm((prev) => ({ ...prev, dob, age: derived != null ? String(derived) : prev.age }));
                    }}
                    className="h-9 mt-0.5"
                    data-testid="reception-new-dob"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sex</label>
                  <select
                    value={form.sex}
                    onChange={(e) => setForm({ ...form, sex: e.target.value })}
                    className="h-9 mt-0.5 w-full border border-slate-300 rounded-md text-sm px-2"
                    data-testid="reception-new-sex"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Referred by</label>
                  <Input value={form.referred_by} onChange={(e) => setForm({ ...form, referred_by: e.target.value })} className="h-9 mt-0.5" data-testid="reception-new-referred_by" />
                  <div className="mt-3"><PatientLanguageSelect value={form.preferred_language} onChange={(v) => setForm({ ...form, preferred_language: v })} testId="reception-new-language" label="Default patient language" /></div>
              </div>
              <Button onClick={() => registerNew(false)} className="w-full bg-pink-600 hover:bg-pink-700 text-white gap-1.5" data-testid="reception-register-btn">
                <UserPlus size={14} /> Register & continue to vitals
              </Button>
            </div>
          )}
        </section>

        {/* RIGHT: vitals + uploads for the selected patient */}
        <section className="bg-white border border-slate-200 rounded-md p-5">
          {!patient ? (
            <div className="text-center py-16 text-slate-500 text-sm">
              <ArrowRight className="mx-auto mb-3 rotate-180 opacity-30" />
              Select a patient on the left to start intake.
            </div>
          ) : (
            <div className="space-y-4" data-testid="reception-intake-form">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Intake for</div>
                  <div className="text-sm font-heading font-semibold text-slate-900">{patient.name} <span className="text-[11px] font-normal text-slate-500 ml-1">({patient.patient_id})</span></div>
                </div>
                <button onClick={() => setPatient(null)} className="text-[11px] text-slate-500 hover:text-slate-800 hover:underline" data-testid="reception-change-patient">Change patient</button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <VField label="Weight (kg)" icon={<Weight size={11} />} value={vitals.weight} onChange={(v) => setVitals({ ...vitals, weight: v })} testid="v-weight" />
                <VField label="Height (cm)" icon={<Ruler size={11} />} value={vitals.height} onChange={(v) => setVitals({ ...vitals, height: v })} testid="v-height" />
                <VField label="Waist (cm)" value={vitals.waist} onChange={(v) => setVitals({ ...vitals, waist: v })} testid="v-waist" />
                <VField label="Systolic BP" value={vitals.sbp} onChange={(v) => setVitals({ ...vitals, sbp: v })} testid="v-sbp" />
                <VField label="Diastolic BP" value={vitals.dbp} onChange={(v) => setVitals({ ...vitals, dbp: v })} testid="v-dbp" />
                <VField label="Pulse (bpm)" icon={<Heart size={11} />} value={vitals.pulse} onChange={(v) => setVitals({ ...vitals, pulse: v })} testid="v-pulse" />
                <VField label="SpO₂ (%)" icon={<Activity size={11} />} value={vitals.spo2} onChange={(v) => setVitals({ ...vitals, spo2: v })} testid="v-spo2" />
                <div className="col-span-3 flex items-center gap-2 text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                  <Droplets size={11} className="text-pink-500" /> BMI (auto): <span className="tabular font-medium text-slate-800">{bmi || "—"}</span>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-2">
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-xs" data-testid="reception-upload-photo">
                  {photoUploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  Upload patient photo (optional)
                  <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={uploadPhoto} className="hidden" />
                </label>
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-xs" data-testid="reception-upload-report">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
                  Upload report file (PDF / image)
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={uploadReport} className="hidden" />
                </label>
                <ManualLabEntry patient={patient} />
              </div>

              <Button onClick={saveIntake} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="reception-save-intake">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save intake — doctor will see it live
              </Button>
              <p className="text-[10px] text-slate-500 text-center">
                Prescription, diagnosis and other clinical fields are only accessible to the doctor.
              </p>
            </div>
          )}
        </section>
      </main>

      {familyConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-md max-w-md w-full p-5" data-testid="family-confirm-dialog">
            <div className="font-heading font-semibold text-slate-900 mb-2">This mobile already has a patient</div>
            <p className="text-xs text-slate-700 mb-3">
              <strong>{form.mobile}</strong> is used by {familyConfirm.family_members.length} existing patient(s). Is <strong>{form.name}</strong> a new family member?
            </p>
            <ul className="text-[11px] bg-slate-50 border border-slate-200 rounded-md p-2 mb-3 space-y-1 max-h-32 overflow-auto">
              {familyConfirm.family_members.map((f) => (
                <li key={f.patient_id} data-testid={`family-existing-${f.patient_id}`}>{f.name} · {f.patient_id}</li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFamilyConfirm(null)}>Cancel</Button>
              <Button onClick={() => registerNew(true)} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="family-confirm-add">Yes, new family member</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VField({ label, value, onChange, icon, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">{icon}{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 mt-0.5 tabular"
        inputMode="decimal"
        data-testid={testid}
      />
    </div>
  );
}
